import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouterLegacy } from 'core/routerUtils';
import Auth from 'core/services/authToken';
import merge from 'lodash/merge';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { withTranslation } from 'react-i18next';
import * as userActions from 'core/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import { RegisterForm, RegisterProfile, Demographic, EmailVerification } from 'features/register/';
import { UsersService, ValidationService } from 'shared/services';
import { errorStrings, validationRules } from 'shared/validations';
import * as rules from 'features/profile/rules';
import { parseQueryString } from 'shared/helpers';
import { UserModel, PersonnelModel } from 'core/userModel';
import './RegisterContainer.scss';

class RegisterContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      user: { ...new UserModel({}), ...new PersonnelModel({}) },
      terms: false,
      termsCopy: props.terms,
      errors: {},
      recaptchaValue: '',
      recaptchaError: false,
    };
    this.props.actions.getAccountTerms();

    this.handleFormChange = this.handleFormChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSubmitEmailPass = this.onSubmitEmailPass.bind(this);
    this.onSubmitDemographic = this.onSubmitDemographic.bind(this);
    this.registerForm = this.registerForm.bind(this);
    this.registerProfile = this.registerProfile.bind(this);
    this.demographicQuestions = this.demographicQuestions.bind(this);
    this.getStep = this.getStep.bind(this);
  }

  componentDidMount() {
    if (Auth.authToken) {
      this.props.history.push('/dashboard');
      return;
    }

    const { step } = this.props.match.params;
    if (
      step &&
      step !== 'email-verification' &&
      (!this.state.user.password || !this.state.user.email)
    ) {
      this.props.history.push('/register');
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tncVersion !== this.props.tncVersion || prevProps.terms !== this.props.terms) {
      this.setState((prevState) => ({
        user: {
          ...prevState.user,
          tncVersion: this.props.tncVersion,
        },
        termsCopy: this.props.terms,
      }));
    }

    if (this.props.cognitoUserCreated && !prevProps.cognitoUserCreated) {
      this.props.history.push('/register/success');
      this.props.actions.resetUserAction();
    }
  }

  isCaptchaChecked = () => {
    if (!this.state.recaptchaValue.length) {
      this.setState({
        recaptchaError: true,
      });
      return false;
    }
    this.setState({
      user: { ...this.state.user, recaptchaCode: this.state.recaptchaValue },
    });
    return true;
  };

  onSubmit(e) {
    e.preventDefault();
    const { user } = this.state;

    const errors = ValidationService.validate(user, validationRules, errorStrings);

    if (isEmpty(errors)) {
      //this.props.history.push('/register/additional-information');
      this.onSubmitDemographic({});
    } else {
      this.setState({
        errors,
      });
    }
  }

  onSubmitEmailPass(e) {
    e.preventDefault();
    const isCaptchaChecked = this.isCaptchaChecked();
    const fields = pick(this.state.user, ['emailAddress', 'password', 'userPool']);
    const { emailAddress, password } = this.state.user;
    fields.terms = this.state.terms;
    const errors = ValidationService.validate(fields, validationRules, errorStrings);

    if (password && emailAddress.includes(password)) {
      errors.password = ['errors.emailCannotContainPassword'];
    }

    if (isEmpty(errors) && this.state.terms && isCaptchaChecked) {
      UsersService.checkUsername(fields.emailAddress).then((response) => {
        if (response.result) {
          this.setState({
            errors: {
              emailAddress: ['errors.accountExists'],
            },
          });
          return;
        }

        this.onSubmitDemographic({ birthDate: '01-01-2000' });
        //this.props.history.push('/register/profile-information');
      });
    } else {
      this.setState({
        errors,
      });
    }
  }

  onSubmitDemographic(data) {
    const user = Object.assign({}, this.state.user, data);
    const cognitoUser = new UserModel(user);
    const personnel = new PersonnelModel(user);
    cognitoUser.password = user.password;
    this.props.actions.createUser(omit(cognitoUser, 'email'), personnel);
  }

  getStep() {
    const { step } = this.props.match.params;
    if (Auth.authToken) {
      this.props.history.push('/dashboard');
    }
    switch (step) {
      case 'profile-information':
        return this.registerProfile();
      case 'additional-information':
        return this.demographicQuestions();
      case 'success':
        return (
          <div>
            <h3 className="mt-30 mb-4 text-center">
              {this.props.t('register.activateAccountTitle')}
            </h3>
            <p className="text-center">
              <b>{this.props.t('register.activateAccountMessage')}</b>
              {this.props.t('register.activateAccountMessage2')}
            </p>
          </div>
        );
      case 'email-verification':
        return this.emailVerification();
      default:
        return this.registerForm();
    }
  }

  handleCaptcha = (recaptchaValue) => {
    this.setState({
      recaptchaValue: recaptchaValue,
      recaptchaError: recaptchaValue.length ? false : true,
    });
  };

  registerForm() {
    return (
      <RegisterForm
        user={this.state.user}
        termsCopy={this.state.termsCopy}
        onChange={this.handleFormChange}
        onSubmit={this.onSubmitEmailPass}
        errors={this.state.errors}
        handleCaptcha={this.handleCaptcha}
        recaptchaError={this.state.recaptchaError}
      />
    );
  }

  registerProfile() {
    return (
      <RegisterProfile
        user={this.state.user}
        onChange={this.handleFormChange}
        onSubmit={this.onSubmit}
        errors={this.state.errors}
      />
    );
  }

  demographicQuestions() {
    return (
      <Demographic
        user={this.state.user}
        onChange={this.handleFormChange}
        onSubmit={this.onSubmitDemographic}
        errors={this.state.errors}
        errorAccount={this.props.error}
      />
    );
  }

  emailVerification() {
    const params = parseQueryString(this.props.location.search);
    if (!params.confirmationCode || !params.username) {
      this.props.history.push('/');
      return null;
    }
    return <EmailVerification hash={params.confirmationCode} username={params.username} />;
  }

  handleFormChange(e) {
    let newState = { ...this.state };
    if (e.target) {
      newState.user = {
        ...this.state.user,
        [e.target.name]: e.target.value,
      };
      newState.user.username = newState.user.email;
    } else {
      newState = merge({}, newState, e);
    }
    const errors = ValidationService.validate(newState.user, rules.onTypeRules, errorStrings);

    if (!isEmpty(errors)) {
      this.setState({
        errors,
      });
      return;
    } else {
      newState.errors = {};
    }

    this.setState(newState);
  }

  render() {
    return <div className="register-container">{this.getStep()}</div>;
  }
}

function mapStateToProps(state) {
  return {
    userCreated: state.users.userCreated,
    cognitoUserCreated: state.users.cognitoUserCreated,
    terms: state.users.terms,
    tncVersion: state.users.tncVersion,
    error: state.users.error,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...userActions, ...staticActions }, dispatch),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(RegisterContainer))
);

RegisterContainer.propTypes = {
  // Props from react-router (via withRouterLegacy)
  match: PropTypes.shape({
    params: PropTypes.shape({
      hash: PropTypes.string,
      step: PropTypes.string,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,

  // Props from mapStateToProps
  userCreated: PropTypes.bool,
  cognitoUserCreated: PropTypes.bool,
  terms: PropTypes.string,
  tncVersion: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

  // Props from mapDispatchToProps
  actions: PropTypes.shape({
    createUser: PropTypes.func.isRequired,
    getAccountTerms: PropTypes.func.isRequired,
    resetUserAction: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

RegisterContainer.defaultProps = {
  terms: '',
  tncVersion: '',
  error: '',
};
